/**
 * <cart-delivery-date> — computes the estimated delivery date range client-side from the
 * merchant-configured min/max business-day delays (blocks/cart-delivery-estimate.liquid).
 *
 * The server renders a locale-correct fallback text node ("Réception estimée sous 3 à 4 jours
 * ouvrés") so the DOM always has real content without JS. Once this component upgrades, it
 * replaces that text with an actual date range ("Réception estimée le lun. 21 sept. – mar. 22
 * sept.") formatted with Intl.DateTimeFormat against the page locale — never a hardcoded string,
 * the prefix itself comes from the translated `data-prefix` attribute.
 *
 * Normalization (both settings are free-standing 0-10 ranges, nothing stops a merchant from
 * setting them the "wrong" way round):
 * - min/max are sorted so a merchant-set min > max never renders end-before-start.
 * - min === max renders a single date, never a repeated "same date – same date".
 * - day 0 still lands on a real business day: if "today" is a weekend, the count starts from the
 *   next business day instead of shipping on a Saturday/Sunday.
 */
class CartDeliveryDate extends HTMLElement {
  connectedCallback() {
    if (typeof Intl === 'undefined' || typeof Intl.DateTimeFormat !== 'function') return;

    const rawMin = Number(this.dataset.minDays) || 0;
    const rawMax = Number(this.dataset.maxDays) || 0;
    const minDays = Math.min(rawMin, rawMax);
    const maxDays = Math.max(rawMin, rawMax);
    const prefix = this.dataset.prefix || '';
    const locale = document.documentElement.lang || undefined;

    const formatter = new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'short' });
    const start = formatter.format(this.#addBusinessDays(new Date(), minDays));
    const range = minDays === maxDays ? start : `${start} – ${formatter.format(this.#addBusinessDays(new Date(), maxDays))}`;

    // Maquette : préfixe en couleur secondaire, dates en gras.
    const prefixNode = document.createElement('span');
    prefixNode.className = 'cart-delivery-estimate__prefix';
    prefixNode.textContent = prefix ? `${prefix} ` : '';
    const dateNode = document.createElement('strong');
    dateNode.textContent = range;
    this.replaceChildren(prefixNode, dateNode);
  }

  /**
   * @param {Date} from
   * @param {number} days
   * @returns {Date}
   */
  #addBusinessDays(from, days) {
    const isWeekend = (d) => d.getDay() === 0 || d.getDay() === 6;
    const date = new Date(from);
    let added = 0;
    while (added < days) {
      date.setDate(date.getDate() + 1);
      if (!isWeekend(date)) added += 1;
    }
    // days === 0 leaves `date` on "today" untouched above: if that lands on a weekend, roll
    // forward to the next business day rather than promising a Saturday/Sunday departure.
    while (isWeekend(date)) {
      date.setDate(date.getDate() + 1);
    }
    return date;
  }
}

if (!customElements.get('cart-delivery-date')) {
  customElements.define('cart-delivery-date', CartDeliveryDate);
}
